/**
 * Environment Configuration Utilities
 * Centralized environment variable management with validation
 */

// import { API_BASE_URL } from '../constants'; // Not used in this file

// ============================================================================
// Environment Variable Validation
// ============================================================================

interface EnvironmentConfig {
  NODE_ENV: 'development' | 'production' | 'test';
  REACT_APP_API_BASE_URL: string;
  REACT_APP_APP_NAME: string;
  REACT_APP_VERSION: string;
  REACT_APP_ENABLE_DEV_TOOLS: boolean;
  REACT_APP_ENABLE_ANALYTICS: boolean;
  REACT_APP_LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error';
  REACT_APP_FEATURE_MARKETING_REPORTS: boolean;
  REACT_APP_FEATURE_COMPLIANCE: boolean;
  REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS: string;
}

// Default values for environment variables
const DEFAULT_CONFIG: EnvironmentConfig = {
  NODE_ENV: 'development',
  // Override logic to ensure /api prefix is always included
  REACT_APP_API_BASE_URL: (() => {
    const envUrl = process.env.REACT_APP_API_BASE_URL;
    if (envUrl) {
      // If explicitly set, use it but ensure it ends with /api
      return envUrl.endsWith('/api')
        ? envUrl
        : `${envUrl.replace(/\/$/, '')}/api`;
    }
    // Otherwise use the production/development logic
    return process.env.NODE_ENV === 'production'
      ? 'https://baseline-backend-prod.onrender.com/api'
      : '/api';
  })(),
  REACT_APP_APP_NAME: 'Gestión de Consumos Energéticos',
  REACT_APP_VERSION: '2.0.0',
  REACT_APP_ENABLE_DEV_TOOLS: true,
  REACT_APP_ENABLE_ANALYTICS: false,
  REACT_APP_LOG_LEVEL: 'info',
  REACT_APP_FEATURE_MARKETING_REPORTS: false,
  REACT_APP_FEATURE_COMPLIANCE: false,
  REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS: '',
};

// ============================================================================
// Environment Configuration Class
// ============================================================================

export class EnvironmentManager {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
  }

  private loadConfig(): EnvironmentConfig {
    return {
      NODE_ENV:
        (process.env.NODE_ENV as EnvironmentConfig['NODE_ENV']) ||
        DEFAULT_CONFIG.NODE_ENV,
      REACT_APP_API_BASE_URL:
        process.env.REACT_APP_API_BASE_URL ||
        DEFAULT_CONFIG.REACT_APP_API_BASE_URL,
      REACT_APP_APP_NAME:
        process.env.REACT_APP_APP_NAME || DEFAULT_CONFIG.REACT_APP_APP_NAME,
      REACT_APP_VERSION:
        process.env.REACT_APP_VERSION || DEFAULT_CONFIG.REACT_APP_VERSION,
      REACT_APP_ENABLE_DEV_TOOLS: this.parseBoolean(
        process.env.REACT_APP_ENABLE_DEV_TOOLS,
        DEFAULT_CONFIG.REACT_APP_ENABLE_DEV_TOOLS,
      ),
      REACT_APP_ENABLE_ANALYTICS: this.parseBoolean(
        process.env.REACT_APP_ENABLE_ANALYTICS,
        DEFAULT_CONFIG.REACT_APP_ENABLE_ANALYTICS,
      ),
      REACT_APP_LOG_LEVEL:
        (process.env
          .REACT_APP_LOG_LEVEL as EnvironmentConfig['REACT_APP_LOG_LEVEL']) ||
        DEFAULT_CONFIG.REACT_APP_LOG_LEVEL,
      REACT_APP_FEATURE_MARKETING_REPORTS: this.parseBoolean(
        process.env.REACT_APP_FEATURE_MARKETING_REPORTS,
        DEFAULT_CONFIG.REACT_APP_FEATURE_MARKETING_REPORTS,
      ),
      REACT_APP_FEATURE_COMPLIANCE: this.parseBoolean(
        process.env.REACT_APP_FEATURE_COMPLIANCE,
        DEFAULT_CONFIG.REACT_APP_FEATURE_COMPLIANCE,
      ),
      REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS:
        process.env.REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS ??
        DEFAULT_CONFIG.REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS,
    };
  }

  private parseBoolean(
    value: string | undefined,
    defaultValue: boolean,
  ): boolean {
    if (value === undefined) return defaultValue;
    return value.toLowerCase() === 'true';
  }

  private validateConfig(): void {
    const { REACT_APP_API_BASE_URL } = this.config;

    // Validate API URL format
    try {
      new URL(REACT_APP_API_BASE_URL);
    } catch {
      console.warn(
        `Invalid API URL: ${REACT_APP_API_BASE_URL}. Using default: ${DEFAULT_CONFIG.REACT_APP_API_BASE_URL}`,
      );
      this.config.REACT_APP_API_BASE_URL =
        DEFAULT_CONFIG.REACT_APP_API_BASE_URL;
    }
  }

  // Getters for environment variables
  get nodeEnv(): EnvironmentConfig['NODE_ENV'] {
    return this.config.NODE_ENV;
  }

  get apiBaseUrl(): string {
    return this.config.REACT_APP_API_BASE_URL;
  }

  get appName(): string {
    return this.config.REACT_APP_APP_NAME;
  }

  get appVersion(): string {
    return this.config.REACT_APP_VERSION;
  }

  get isDevelopment(): boolean {
    return this.config.NODE_ENV === 'development';
  }

  get isProduction(): boolean {
    return this.config.NODE_ENV === 'production';
  }

  get isTest(): boolean {
    return this.config.NODE_ENV === 'test';
  }

  get enableDevTools(): boolean {
    return this.config.REACT_APP_ENABLE_DEV_TOOLS && this.isDevelopment;
  }

  get enableAnalytics(): boolean {
    return this.config.REACT_APP_ENABLE_ANALYTICS;
  }

  get featureMarketingReports(): boolean {
    return this.config.REACT_APP_FEATURE_MARKETING_REPORTS;
  }

  get featureCompliance(): boolean {
    return this.config.REACT_APP_FEATURE_COMPLIANCE;
  }

  get marketingReportAllowedHosts(): readonly string[] {
    return this.config.REACT_APP_MARKETING_REPORT_ALLOWED_HOSTS.split(',')
      .map((host) => host.trim())
      .filter(Boolean);
  }

  get logLevel(): EnvironmentConfig['REACT_APP_LOG_LEVEL'] {
    return this.config.REACT_APP_LOG_LEVEL;
  }

  // Utility methods
  shouldLog(level: EnvironmentConfig['REACT_APP_LOG_LEVEL']): boolean {
    const levels = ['debug', 'info', 'warn', 'error'];
    const currentLevelIndex = levels.indexOf(this.config.REACT_APP_LOG_LEVEL);
    const checkLevelIndex = levels.indexOf(level);
    return checkLevelIndex >= currentLevelIndex;
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

export const env = new EnvironmentManager();

// ============================================================================
// Environment Validation Helper
// ============================================================================

export function validateEnvironment(): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  // Check required environment variables
  if (!env.apiBaseUrl) {
    errors.push('REACT_APP_API_BASE_URL is required');
  }

  // Validate API URL format
  try {
    new URL(env.apiBaseUrl);
  } catch {
    errors.push('REACT_APP_API_BASE_URL must be a valid URL');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

// ============================================================================
// Development Helpers
// ============================================================================

export interface EnvironmentInfo {
  nodeEnv: string;
  apiBaseUrl: string;
  appName: string;
  appVersion: string;
  enableDevTools: boolean;
  enableAnalytics: boolean;
  logLevel: string;
  timestamp: string;
  userAgent: string;
  url: string;
}

export function getEnvironmentInfo(): EnvironmentInfo {
  return {
    nodeEnv: env.nodeEnv,
    apiBaseUrl: env.apiBaseUrl,
    appName: env.appName,
    appVersion: env.appVersion,
    enableDevTools: env.enableDevTools,
    enableAnalytics: env.enableAnalytics,
    logLevel: env.logLevel,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
  };
}
